import { useMemo, useState, type DragEvent } from "react";
import { Bus, Truck, Zap, FileText, Luggage, X as XIcon } from "lucide-react";
import { Button, EmptyState, Modal, ActionModal, PageHeader, Picker, Spinner, DialogCancel } from "@cntrl/ui";
import { useIsPro } from "../../app/bandContext.js";
import { RoleLabel } from "../contacts/RoleLabel.js";
import { ApiError } from "../../app/hooks/apiError.js";
import { pushToast } from "../../app/toasts.js";
import { PersonAvatar } from "../shared/PersonAvatar.js";
import { CREW_PARTIES, crewPartyLabel } from "../contacts/crewParty.js";
import { bunkCode, bunkGroup, bunkSide, bunkTier, bunkLong, SELECTABLE_BUNK_COUNTS } from "./bunkGeometry.js";
import { BusStockPanel, FleetShoppingList } from "./BusStockPanel.js";
import {
  useFleet, useAddVehicle, useUpdateVehicle, useRemoveVehicle, usePlaceBunk, useClearBunk,
  useSetBunkJunk, useClearBus, useAutoFill,
  type FleetView, type VehicleView, type UpdateVehicleRequest,
} from "../../app/hooks/useBuses.js";
import styles from "./Buses.module.scss";

const BUNK_MIME = "application/x-cntrl-bunk-crew";
/** The reusable "Junk Bunk" drag marker. Unlike a crew member, it is never used up: it lives in a fixed row
 *  at the foot of the rail and drops onto any open bunk to mark it as luggage storage. A separate MIME (not a
 *  sentinel personId) keeps the drop handlers honest — a crew id and a junk marker are different kinds. */
const JUNK_MIME = "application/x-cntrl-bunk-junk";
const initials = (name: string) =>
  name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "?";
const partyVar = (party: string) => `var(--party-${party})`;

/** The Buses SCREEN — Pro AND AV-or-TM (the full chart). A Standard band is SOLD; a non-editor is told where
 *  their bunk lives. One section-level gate, never per-widget. */
export function BusesScreen() {
  if (!useIsPro()) return <BusesUpsell />;
  return <BusesEditor />;
}

function BusesUpsell() {
  return (
    <div className={styles.main}>
      <PageHeader title="Buses & Trucks" />
      <EmptyState
        title="Buses & Trucks is an Artist CNTRL Pro feature"
        description="Map the tour's rolling stock and who sleeps where — drag-and-drop bunk assignments, a
          print-ready bus doc, driver, codes and Wi-Fi per coach. Upgrade to Pro to build your fleet."
      />
    </div>
  );
}

function BusesEditor() {
  const fleet = useFleet();
  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState<VehicleView | null>(null);
  const [autoFilling, setAutoFilling] = useState<VehicleView | null>(null);
  const [docFor, setDocFor] = useState<VehicleView | null>(null);
  const [dragPid, setDragPid] = useState<string | null>(null);
  const [q, setQ] = useState("");

  if (fleet.isLoading) {
    return <div className={styles.main}><PageHeader title="Buses & Trucks" /><Spinner /></div>;
  }
  if (fleet.error) {
    // 403 = not an AV/TM seat. The full chart is theirs alone; a plain crew member reaches their own bunk
    // through the four My Bunk surfaces, never here. Not an error card — a plain statement.
    const notEditor = fleet.error instanceof ApiError && fleet.error.status === 403;
    return (
      <div className={styles.main}>
        <PageHeader title="Buses & Trucks" />
        <EmptyState
          title={notEditor ? "The bus chart is managed by your tour manager" : "Couldn't load the fleet"}
          description={notEditor
            ? "Your own bunk shows on your dashboard, Day of Show and Show Logistics. Only an AV owner or Tour Manager sees and edits the whole fleet."
            : "Something went wrong loading the buses. Try again in a moment."}
        />
      </div>
    );
  }

  const data = fleet.data as FleetView;
  const { vehicles, crew, stats } = data;

  return (
    <div className={styles.screen}>
      <div className={styles.main}>
        <PageHeader title="Buses & Trucks" />

        {vehicles.length === 0 ? (
          // No empty-state copy (Ryan, ART-214 — "don't need any of this text"); the two Add cards are the
          // whole affordance, and they are bigger now.
          <div className={styles.emptyFleet}>
            <div className={styles.bigadd}>
              <button className={styles.bigbtn} onClick={() => setAddOpen(true)}>
                <span className={styles.bigbtnP}><Bus size={30} /></span>
                <span className={styles.bigbtnL}>Add a bus</span>
                <span className={styles.bigbtnS}>Bunks + driver</span>
              </button>
              <AddTruckButton />
            </div>
          </div>
        ) : (
          <>
            <FleetStatsRow stats={stats} />
            <div className={styles.tools}>
              <Button size="sm" variant="ghost" onClick={() => setAddOpen(true)}><Bus size={14} /> Add bus</Button>
              <AddTruckButton compact />
            </div>
            {vehicles.map((v) => (
              <VehicleCard
                key={v.vehicleId}
                v={v}
                crew={crew}
                dragPid={dragPid}
                onDragStart={setDragPid}
                onDragEnd={() => setDragPid(null)}
                onEdit={() => setEditing(v)}
                onAutoFill={() => setAutoFilling(v)}
                onDoc={() => setDocFor(v)}
              />
            ))}
            {/* TM view: the whole fleet's asks rolled into one shopping list (§7.6). */}
            <FleetShoppingList />
          </>
        )}
      </div>

      <CrewRail crew={crew} q={q} onQ={setQ} onDragStart={setDragPid} onDragEnd={() => setDragPid(null)} />

      {addOpen && <AddBusModal onClose={() => setAddOpen(false)} />}
      {editing && <VehicleEditModal v={editing} onClose={() => setEditing(null)} />}
      {autoFilling && <AutoFillModal v={autoFilling} crew={crew} onClose={() => setAutoFilling(null)} />}
      {docFor && <BusDocModal v={docFor} crew={crew} onClose={() => setDocFor(null)} />}
    </div>
  );
}

function AddTruckButton({ compact }: { compact?: boolean }) {
  const add = useAddVehicle();
  const onAdd = () => add.mutate({ kind: "truck", bunkCount: null, name: null }, {
    onError: (e) => pushToast(e.message, "error"),
  });
  if (compact) return <Button size="sm" variant="ghost" onClick={onAdd}><Truck size={14} /> Add truck</Button>;
  return (
    <button className={styles.bigbtn} onClick={onAdd}>
      <span className={styles.bigbtnP}><Truck size={30} /></span>
      <span className={styles.bigbtnL}>Add a truck</span>
      <span className={styles.bigbtnS}>Gear + trailer</span>
    </button>
  );
}

function FleetStatsRow({ stats }: { stats: FleetView["stats"] }) {
  const open = stats.bunkTotal - stats.placed;
  return (
    <div className={styles.fstats}>
      <div className={`${styles.fstat} ${styles.ink}`}>
        <div className={styles.fsL}>Fleet</div>
        <div className={styles.fsV}>{stats.vehicles}</div>
        <div className={styles.fsS}>{stats.buses} bus{stats.buses === 1 ? "" : "es"} · {stats.trucks} truck{stats.trucks === 1 ? "" : "s"}</div>
      </div>
      <div className={styles.fstat}>
        <div className={styles.fsL}>Bunks assigned</div>
        <div className={styles.fsV}>{stats.placed}<span style={{ fontSize: 15, color: "var(--ac-color-t3)" }}>/{stats.bunkTotal}</span></div>
        <div className={styles.fsS}>{open} open</div>
      </div>
      <div className={`${styles.fstat} ${stats.crewUnassigned ? styles.warn : ""}`}>
        <div className={styles.fsL}>Crew unassigned</div>
        <div className={styles.fsV}>{stats.crewUnassigned}</div>
        <div className={styles.fsS}>{stats.crewUnassigned ? "Still need a bunk" : "Everyone placed"}</div>
      </div>
      <div className={styles.fstat}>
        <div className={styles.fsL}>Beds free</div>
        <div className={styles.fsV}>{open}</div>
        <div className={styles.fsS}>{open ? "Open bunks across the fleet" : "Fleet is full"}</div>
      </div>
    </div>
  );
}

// ---- vehicle card -----------------------------------------------------------------------------------------

function VehicleCard({ v, crew, dragPid, onDragStart, onDragEnd, onEdit, onAutoFill, onDoc }: {
  v: VehicleView;
  crew: FleetView["crew"];
  dragPid: string | null;
  onDragStart: (pid: string) => void;
  onDragEnd: () => void;
  onEdit: () => void;
  onAutoFill: () => void;
  onDoc: () => void;
}) {
  const remove = useRemoveVehicle();
  const clearBus = useClearBus();
  const isBus = v.kind === "bus";
  const pct = v.capacity ? Math.round((v.filled / v.capacity) * 100) : 0;
  const crewById = useMemo(() => new Map(crew.map((c) => [c.personId, c])), [crew]);

  return (
    <div className={styles.veh}>
      <div className={styles.vehHd}>
        <span className={`${styles.vehNo} ${isBus ? "" : styles.truck}`}>{isBus ? v.no : <Truck size={16} />}</span>
        <div>
          <div className={styles.vehNm}>{v.name}</div>
          <div className={styles.vehTag}>
            {isBus
              ? <>{v.tag ? <><b>{v.tag}</b> · </> : null}{v.capacity} bunks · {v.filled}/{v.capacity} assigned</>
              : "Cargo · no bunks"}
          </div>
        </div>
        <div className={styles.vehActs}>
          {isBus && (
            <span className={styles.cap}>
              <span className={styles.capbar}>
                <i className={`${styles.capbarI} ${pct >= 100 ? styles.full : pct >= 70 ? styles.mid : ""}`} style={{ width: `${Math.min(pct, 100)}%` }} />
              </span>{pct}%
            </span>
          )}
          {isBus && <Button size="sm" variant="ghost" onClick={onAutoFill}><Zap size={12} /> Auto-fill</Button>}
          {isBus && <Button size="sm" variant="ghost" onClick={onDoc}><FileText size={12} /> Bus doc</Button>}
          <Button size="sm" variant="ghost" onClick={onEdit}>Edit</Button>
          {isBus && <Button size="sm" variant="ghost" onClick={() => clearBus.mutate(v.vehicleId)}>Clear</Button>}
          <Button size="sm" variant="ghost" tone="danger" onClick={() => remove.mutate(v.vehicleId)} aria-label="Remove"><XIcon size={13} /></Button>
        </div>
      </div>
      <div className={styles.vehBd}>
        {isBus && <Floorplan v={v} crewById={crewById} dragPid={dragPid} onDragStart={onDragStart} onDragEnd={onDragEnd} />}
        <VehicleInfo v={v} isBus={isBus} />
        {isBus && <RosterTable v={v} crewById={crewById} />}
        {isBus && <BusStockPanel vehicleId={v.vehicleId} />}
      </div>
    </div>
  );
}

function Floorplan({ v, crewById, dragPid, onDragStart, onDragEnd }: {
  v: VehicleView;
  crewById: Map<string, FleetView["crew"][number]>;
  dragPid: string | null;
  onDragStart: (pid: string) => void;
  onDragEnd: () => void;
}) {
  const place = usePlaceBunk();
  const clearBunk = useClearBunk();
  const setJunk = useSetBunkJunk();
  const bySlot = useMemo(() => new Map(v.bunks.map((b) => [b.slot, b])), [v.bunks]);

  const drop = (slot: number) => (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.currentTarget.removeAttribute("data-drag-over");
    e.currentTarget.removeAttribute("data-drag-swap");
    const pid = e.dataTransfer.getData(BUNK_MIME);
    if (pid) {
      place.mutate({ personId: pid, vehicleId: v.vehicleId, slot }, {
        onError: (err) => pushToast(err.message, "error"),
      });
      return;
    }
    if (e.dataTransfer.getData(JUNK_MIME)) {
      setJunk.mutate({ vehicleId: v.vehicleId, slot, junk: true }, {
        onError: (err) => pushToast(err.message, "error"),
      });
    }
  };
  const over = (occupied: boolean) => (e: DragEvent<HTMLDivElement>) => {
    const isCrew = e.dataTransfer.types.includes(BUNK_MIME);
    const isJunk = e.dataTransfer.types.includes(JUNK_MIME);
    // A junk marker only lands on an EMPTY bunk — it never swaps onto a person (unassign first).
    if (!isCrew && !isJunk) return;
    if (isJunk && occupied) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    e.currentTarget.setAttribute(occupied ? "data-drag-swap" : "data-drag-over", "");
  };
  const leave = (e: DragEvent<HTMLDivElement>) => {
    e.currentTarget.removeAttribute("data-drag-over");
    e.currentTarget.removeAttribute("data-drag-swap");
  };

  const cell = (slot: number) => {
    const b = bySlot.get(slot);
    if (!b) return null;
    const occ = b.personId ? crewById.get(b.personId) : null;
    const taken = !!occ;
    const junk = !taken && b.junk;
    return (
      <div
        key={slot}
        className={`${styles.bunk} ${taken ? styles.taken : junk ? styles.junk : styles.open}`}
        style={occ ? ({ ["--dep" as string]: partyVar(occ.party) }) : undefined}
        title={bunkLong(slot)}
        draggable={taken || undefined}
        onDragStart={taken ? (e) => { e.dataTransfer.setData(BUNK_MIME, occ!.personId); e.dataTransfer.effectAllowed = "move"; onDragStart(occ!.personId); } : undefined}
        onDragEnd={taken ? onDragEnd : undefined}
        onDragOver={junk ? undefined : over(taken && occ!.personId !== dragPid)}
        onDragLeave={junk ? undefined : leave}
        onDrop={junk ? undefined : drop(slot)}
      >
        <span className={styles.bunkC}>{bunkCode(slot)}</span>
        {taken ? (
          <>
            <span className={styles.bunkWho}>
              <PersonAvatar personId={occ!.personId} avatarMediaId={occ!.avatarMediaId} initials={initials(occ!.displayName)} size="xs" />
              <span style={{ minWidth: 0, flex: 1 }}>
                <span className={styles.bunkNm}>{occ!.displayName}</span>
                <span className={styles.bunkTi}>{crewPartyLabel(occ!.party)}</span>
              </span>
            </span>
            <button className={styles.bunkX} title="Unassign" onClick={() => clearBunk.mutate({ vehicleId: v.vehicleId, slot })}><XIcon size={12} /></button>
          </>
        ) : junk ? (
          <>
            <span className={`${styles.bunkHint} ${styles.bunkJunk}`}><Luggage size={12} /> Junk bunk</span>
            <button className={styles.bunkX} title="Clear junk bunk" onClick={() => setJunk.mutate({ vehicleId: v.vehicleId, slot, junk: false })}><XIcon size={12} /></button>
          </>
        ) : (
          <span className={styles.bunkHint}>drop here</span>
        )}
      </div>
    );
  };

  const sideBlock = (side: "D" | "P") => (
    <div className={styles.baySects}>
      {(["F", "B"] as const).map((g) => (
        <div key={g} className={styles.sect}>
          <div className={styles.sectL}>{g === "F" ? "Front" : "Back"}</div>
          {(["T", "M", "B"] as const).map((t) => {
            const slot = v.bunks.find((b) => bunkSide(b.slot) === side && bunkGroup(b.slot) === g && bunkTier(b.slot) === t)?.slot;
            return slot === undefined ? <div key={t} /> : cell(slot);
          })}
        </div>
      ))}
    </div>
  );

  return (
    <div className={styles.plan}>
      <div className={styles.nose}>Cockpit<br />Driver</div>
      <div className={styles.bay}>
        <div className={styles.bayBand}><div className={styles.baySide}>Passenger side</div>{sideBlock("P")}</div>
        <div className={styles.bayAisle}><span>aisle</span></div>
        <div className={styles.bayBand}><div className={styles.baySide}>Driver side</div>{sideBlock("D")}</div>
      </div>
    </div>
  );
}

function VehicleInfo({ v, isBus }: { v: VehicleView; isBus: boolean }) {
  return (
    <div className={styles.iblocks}>
      <div className={styles.ib}>
        <div className={styles.ibL}>Driver</div>
        <InfoRow k="Name" value={v.driver} />
        <InfoRow k="Phone" value={v.driverPhone} mono />
      </div>
      {isBus ? (
        <>
          <div className={styles.ib}>
            <div className={styles.ibL}>Codes</div>
            <InfoRow k="Door" value={v.door} mono blur />
            <InfoRow k="Bay" value={v.bay} mono blur />
            {v.lockNote && <div className={styles.ibNote}>{v.lockNote}</div>}
          </div>
          <div className={styles.ib}>
            <div className={styles.ibL}>Wi-Fi</div>
            <InfoRow k="Network" value={v.wifi} mono />
            <InfoRow k="Password" value={v.wifiPassword} mono copy />
          </div>
        </>
      ) : (
        <>
          <div className={styles.ib}>
            <div className={styles.ibL}>Equipment</div>
            <InfoRow k="Trailer #" value={v.trailer} mono />
            <InfoRow k="Door code" value={v.door} mono blur />
          </div>
          <div className={styles.ib}>
            <div className={styles.ibL}>Load</div>
            {v.cargo ? <div className={styles.ibNote} style={{ border: "none", margin: 0, padding: 0 }}>{v.cargo}</div>
              : <div className={styles.ibNote} style={{ border: "none", margin: 0, padding: 0, color: "var(--ac-color-muted)" }}>No load noted</div>}
          </div>
        </>
      )}
    </div>
  );
}

/** A read row. `blur` hides a code until tapped; `copy` never prints the value (copy-to-clipboard only) —
 *  the §7.5 security posture. */
function InfoRow({ k, value, mono, blur, copy }: { k: string; value?: string | null; mono?: boolean; blur?: boolean; copy?: boolean }) {
  const [shown, setShown] = useState(false);
  const has = !!value;
  if (copy) {
    return (
      <div className={styles.ibRow}>
        <span className={styles.ibK}>{k}</span>
        {has
          ? <button className={`${styles.ibV} ${mono ? styles.mono : ""}`} style={{ cursor: "copy", background: "none", border: "none" }}
              onClick={() => { void navigator.clipboard?.writeText(value!); pushToast("Copied", "success"); }}>Copy</button>
          : <span className={`${styles.ibV} ${styles.ph}`}>—</span>}
      </div>
    );
  }
  return (
    <div className={styles.ibRow}>
      <span className={styles.ibK}>{k}</span>
      {!has
        ? <span className={`${styles.ibV} ${styles.ph}`}>—</span>
        : blur
          ? <button
              type="button"
              className={`${styles.ibV} ${mono ? styles.mono : ""} ${styles.blur} ${shown ? styles.shown : ""}`}
              style={{ border: "none", background: "none", font: "inherit", cursor: "pointer", textAlign: "right" }}
              aria-label={shown ? undefined : `Reveal ${k.toLowerCase()}`}
              onClick={() => setShown((s) => !s)}
            >{value}</button>
          : <span className={`${styles.ibV} ${mono ? styles.mono : ""}`}>{value}</span>}
    </div>
  );
}

function RosterTable({ v, crewById }: { v: VehicleView; crewById: Map<string, FleetView["crew"][number]> }) {
  const rank = { B: 0, M: 1, T: 2 } as const;
  const rows = v.bunks
    .filter((b) => b.personId)
    .map((b) => ({ b, p: crewById.get(b.personId!) }))
    .filter((r) => r.p)
    .sort((x, y) => (rank[bunkTier(x.b.slot)] - rank[bunkTier(y.b.slot)]) || x.p!.displayName.localeCompare(y.p!.displayName));
  const [open, setOpen] = useState(true);
  return (
    <div className={styles.rostwrap}>
      <button className={styles.rostHd} onClick={() => setOpen((o) => !o)}>
        <h4>Sleeping arrangements</h4>
        <span style={{ marginLeft: "auto", fontFamily: "var(--ac-font-mono)", fontSize: 9, color: "var(--ac-color-text-subtle)" }}>
          {v.filled} of {v.capacity}
        </span>
      </button>
      {open && (
        <table className={styles.rost}>
          <thead><tr><th>#</th><th>Name</th><th>Party</th><th>Bunk</th></tr></thead>
          <tbody>
            {rows.length === 0
              ? <tr><td colSpan={4} className={styles.rostEmpty}>Nobody assigned yet.</td></tr>
              : rows.map((r, i) => (
                <tr key={r.b.slot}>
                  <td className={styles.rostN}>{i + 1}</td>
                  <td style={{ fontWeight: 600 }}>{r.p!.displayName}</td>
                  <td style={{ color: "var(--ac-color-t2)" }}>{crewPartyLabel(r.p!.party)}</td>
                  <td className={styles.rostBk}>{bunkCode(r.b.slot)}</td>
                </tr>
              ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ---- crew rail --------------------------------------------------------------------------------------------

function CrewRail({ crew, q, onQ, onDragStart, onDragEnd }: {
  crew: FleetView["crew"];
  q: string;
  onQ: (q: string) => void;
  onDragStart: (pid: string) => void;
  onDragEnd: () => void;
}) {
  const ql = q.trim().toLowerCase();
  // No party filter chips: the list is already grouped by party below, so a chip row over the top of a
  // grouped list is a second control saying what the headings already say. The search box stays.
  const filtered = crew.filter((c) => !ql || c.displayName.toLowerCase().includes(ql));
  // group by party, in the canonical party order
  const groups = CREW_PARTIES.map((p) => ({ party: p, members: filtered.filter((c) => c.party === p.key) }))
    .filter((g) => g.members.length > 0);

  return (
    <aside className={styles.crew}>
      <div className={styles.crewHd}>
        <div className={styles.crewT}>
          <h2 className={styles.crewTitle}>Crew</h2>
          <span className={styles.crewCt}>{crew.length} people</span>
        </div>
        <input
          className={styles.ibV}
          style={{ width: "100%", marginTop: 11, padding: "7px 10px", border: "1px solid var(--ac-color-line-strong)", borderRadius: "var(--ac-radius-sm)", background: "var(--ac-color-glow)", fontWeight: 400, textAlign: "left" }}
          placeholder="Search crew…"
          value={q}
          onChange={(e) => onQ(e.target.value)}
        />
      </div>
      <div className={styles.crewList}>
        {groups.length === 0 && <div className={styles.rostEmpty}>No one matches that filter.</div>}
        {groups.map((g) => {
          const openCount = g.members.filter((m) => !m.assigned).length;
          return (
            <div key={g.party.key}>
              <div className={styles.crewGrp}>{g.party.label} · {openCount} of {g.members.length} open</div>
              {g.members.map((c) => {
                return (
                  <div
                    key={c.personId}
                    className={`${styles.pcard} ${c.assigned ? styles.placed : ""}`}
                    draggable={!c.assigned}
                    onDragStart={!c.assigned ? (e) => { e.dataTransfer.setData(BUNK_MIME, c.personId); e.dataTransfer.effectAllowed = "move"; onDragStart(c.personId); } : undefined}
                    onDragEnd={!c.assigned ? onDragEnd : undefined}
                  >
                    <PersonAvatar personId={c.personId} avatarMediaId={c.avatarMediaId} initials={initials(c.displayName)} size="sm" />
                    <span className={styles.pcardM}>
                      <span className={styles.pcardNm}>{c.displayName}</span>
                      {/* ART-171: RoleLabel from the titleKey — resolves the words AND the AV mark for a
                          full-access member (a bus crew row carries only the title). */}
                      <span className={styles.pcardTi}><RoleLabel titleKey={c.titleKey} /></span>
                    </span>
                    {c.assigned && <span className={styles.pcardOn}>{c.assigned.vehicleName.replace(/^Bus /, "B")} {c.assigned.code}</span>}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
      {/* Fixed to the foot of the rail — the crew list scrolls UNDER it, so it is always reachable without
          scrolling. Reusable: it is not a crew member, so a drop never uses it up. */}
      <div className={styles.crewFoot}>
        <div
          className={styles.junkChip}
          draggable
          onDragStart={(e) => { e.dataTransfer.setData(JUNK_MIME, "1"); e.dataTransfer.effectAllowed = "move"; }}
        >
          <span className={styles.junkChipIco}><Luggage size={16} /></span>
          <span className={styles.pcardM}>
            <span className={styles.pcardNm}>Junk Bunk</span>
          </span>
        </div>
      </div>
    </aside>
  );
}

// ---- modals -----------------------------------------------------------------------------------------------

function AddBusModal({ onClose }: { onClose: () => void }) {
  const add = useAddVehicle();
  const [count, setCount] = useState(12);
  return (
    <ActionModal
      title="Add a bus"
      confirmLabel="Add bus"
      loading={add.isPending}
      onCancel={onClose}
      onConfirm={() => add.mutate({ kind: "bus", bunkCount: count, name: null }, {
        onSuccess: onClose,
        onError: (e) => pushToast(e.message, "error"),
      })}
    >
      <div style={{ fontFamily: "var(--ac-font-mono)", fontSize: 9, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--ac-color-t3)", marginBottom: 8 }}>Bunks</div>
      <div style={{ display: "flex", gap: 8 }}>
        {SELECTABLE_BUNK_COUNTS.map((n) => (
          <button
            key={n}
            onClick={() => setCount(n)}
            style={{
              flex: 1, padding: "10px 0", borderRadius: "var(--ac-radius-sm)", cursor: "pointer",
              border: `1px solid ${count === n ? "var(--ac-color-accent)" : "var(--ac-color-line-strong)"}`,
              background: count === n ? "var(--ac-color-accent-wash)" : "var(--ac-color-paper)",
              fontFamily: "var(--ac-font-display)", fontSize: 16,
            }}
          >{n}</button>
        ))}
      </div>
    </ActionModal>
  );
}

const FIELD_STYLE: React.CSSProperties = {
  width: "100%", padding: "9px 11px", border: "1px solid var(--ac-color-line-strong)",
  borderRadius: "var(--ac-radius-sm)", background: "var(--ac-color-glow)", fontSize: 12.5,
};
function Field({ label, value, onChange, mono }: { label: string; value: string; onChange: (v: string) => void; mono?: boolean }) {
  return (
    <label style={{ display: "block", marginBottom: 12 }}>
      <span style={{ display: "block", fontFamily: "var(--ac-font-mono)", fontSize: 8.5, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--ac-color-t3)", marginBottom: 6 }}>{label}</span>
      <input style={{ ...FIELD_STYLE, fontFamily: mono ? "var(--ac-font-mono)" : "inherit" }} value={value} onChange={(e) => onChange(e.target.value)} />
    </label>
  );
}

function VehicleEditModal({ v, onClose }: { v: VehicleView; onClose: () => void }) {
  const update = useUpdateVehicle();
  const isBus = v.kind === "bus";
  const [f, setF] = useState<UpdateVehicleRequest>({
    name: v.name, driver: v.driver ?? "", driverPhone: v.driverPhone ?? "", door: v.door ?? "",
    bay: v.bay ?? "", wifi: v.wifi ?? "", wifiPassword: v.wifiPassword ?? "", lockNote: v.lockNote ?? "",
    trailer: v.trailer ?? "", cargo: v.cargo ?? "",
  });
  const set = (k: keyof UpdateVehicleRequest) => (val: string) => setF((p) => ({ ...p, [k]: val }));
  const save = () => update.mutate({ vehicleId: v.vehicleId, version: v.version, body: f }, {
    onSuccess: onClose,
    onError: (e) => pushToast(e instanceof ApiError && e.status === 409 ? "This bus changed since you opened it — reopen and reapply." : e.message, "error"),
  });
  return (
    <Modal
      dirty="auto"
      title={v.name}
      subtitle={isBus ? "Driver · codes · Wi-Fi" : "Driver · trailer · load"}
      onClose={onClose}
      size="md"
      footer={<><DialogCancel /><Button onClick={save} loading={update.isPending}>Save</Button></>}
    >
      <Field label="Name" value={f.name ?? ""} onChange={set("name")} />
      <Field label="Driver" value={f.driver ?? ""} onChange={set("driver")} />
      <Field label="Driver phone" value={f.driverPhone ?? ""} onChange={set("driverPhone")} mono />
      <Field label="Door code" value={f.door ?? ""} onChange={set("door")} mono />
      {isBus ? (
        <>
          <Field label="Bay code" value={f.bay ?? ""} onChange={set("bay")} mono />
          <Field label="Wi-Fi network" value={f.wifi ?? ""} onChange={set("wifi")} mono />
          <Field label="Wi-Fi password" value={f.wifiPassword ?? ""} onChange={set("wifiPassword")} mono />
          <Field label="Lock note" value={f.lockNote ?? ""} onChange={set("lockNote")} />
        </>
      ) : (
        <>
          <Field label="Trailer #" value={f.trailer ?? ""} onChange={set("trailer")} mono />
          <Field label="Load" value={f.cargo ?? ""} onChange={set("cargo")} />
        </>
      )}
    </Modal>
  );
}

function AutoFillModal({ v, crew, onClose }: { v: VehicleView; crew: FleetView["crew"]; onClose: () => void }) {
  const autoFill = useAutoFill();
  const [party, setParty] = useState<string>("all");
  const open = v.bunks.filter((b) => !b.personId && !b.junk).length;
  const unassignedByParty = (key: string) => crew.filter((c) => !c.assigned && (key === "all" || c.party === key)).length;
  return (
    <ActionModal
      title="Auto-fill bunks"
      confirmLabel={`Fill ${open} bunk${open === 1 ? "" : "s"}`}
      confirmDisabled={open === 0}
      loading={autoFill.isPending}
      onCancel={onClose}
      onConfirm={() => autoFill.mutate({ vehicleId: v.vehicleId, party: party === "all" ? null : party }, {
        onSuccess: (r) => { pushToast(`Placed ${r.placed ?? 0}.`, "success"); onClose(); },
        onError: (e) => pushToast(e.message, "error"),
      })}
    >
      <p style={{ fontSize: 12.5, color: "var(--ac-color-t2)", marginTop: 0 }}>
        Bottoms fill first, front before back, passenger before driver. {open} open bunk{open === 1 ? "" : "s"}.
      </p>
      <Picker
        label="Pool"
        value={party}
        onChange={setParty}
        options={[
          { value: "all", label: `Anyone unassigned — ${unassignedByParty("all")}` },
          ...CREW_PARTIES.map((p) => {
            const n = unassignedByParty(p.key);
            return { value: p.key, label: `${p.label} — ${n} unassigned`, disabled: n === 0 };
          }),
        ]}
      />
    </ActionModal>
  );
}

function BusDocModal({ v, crew, onClose }: { v: VehicleView; crew: FleetView["crew"]; onClose: () => void }) {
  const crewById = useMemo(() => new Map(crew.map((c) => [c.personId, c])), [crew]);
  const rank = { B: 0, M: 1, T: 2 } as const;
  const rows = v.bunks
    .filter((b) => b.personId)
    .map((b) => ({ b, p: crewById.get(b.personId!) }))
    .filter((r) => r.p)
    .sort((x, y) => (rank[bunkTier(x.b.slot)] - rank[bunkTier(y.b.slot)]) || x.p!.displayName.localeCompare(y.p!.displayName));
  const dash = (s?: string | null) => s || "—";
  const printDoc = () => {
    const w = window.open("", "_blank", "width=760,height=900");
    if (!w) return;
    const body = rows.map((r, i) => `<tr><td>${i + 1}</td><td>${r.p!.displayName}</td><td>${crewPartyLabel(r.p!.party)}</td><td>${bunkCode(r.b.slot)}</td></tr>`).join("");
    w.document.write(`<html><head><title>${v.name}</title><style>
      body{font-family:Inter,system-ui,sans-serif;padding:24px;color:#0e0c0b}
      .t{background:#ece9e3;border:1px solid #ccc;padding:9px;text-align:center;font-weight:800;font-size:16px}
      table{width:100%;border-collapse:collapse;margin-top:9px;font-size:12px}
      th{background:#8B1A2B;color:#fff;padding:7px 9px;text-transform:uppercase;font-size:9px;letter-spacing:.1em}
      td{padding:6px 9px;border-bottom:1px dotted #ccc;text-align:center}
      .g{display:grid;grid-template-columns:1fr 1fr;margin-top:14px;border:1px solid #ccc}
      .c{padding:10px;text-align:center;border-right:1px solid #ccc}.k{font-weight:700}
    </style></head><body>
      <div class="t">${v.name.toUpperCase()}${v.tag ? " — " + v.tag : ""}</div>
      <table><thead><tr><th>#</th><th>Name</th><th>Party</th><th>Bunk</th></tr></thead><tbody>${body || '<tr><td colspan="4">No one assigned</td></tr>'}</tbody></table>
      <div class="g"><div class="c"><div class="k">DRIVER</div><div>${dash(v.driver)}</div><div>${dash(v.driverPhone)}</div></div><div class="c"><div class="k">DOOR / BAY</div><div>${dash(v.door)} / ${dash(v.bay)}</div></div></div>
      <div class="g"><div class="c"><div class="k">WIFI</div><div>${dash(v.wifi)}</div></div><div class="c"><div class="k">PASSWORD</div><div>${v.wifiPassword ? "In the app" : "—"}</div></div></div>
      ${v.lockNote ? `<div class="g"><div class="c" style="grid-column:1/-1">${v.lockNote}</div></div>` : ""}
    </body></html>`);
    w.document.close();
    w.focus();
    w.print();
  };
  return (
    <Modal
      title="Bus doc"
      subtitle={v.name}
      onClose={onClose}
      size="lg"
      footer={<><Button variant="ghost" onClick={onClose}>Close</Button><Button onClick={printDoc}>Print</Button></>}
    >
      <div className={styles.doc}>
        <div className={styles.docTtl}>{v.name.toUpperCase()}{v.tag ? ` — ${v.tag}` : ""}</div>
        <table>
          <thead><tr><th>#</th><th>Name</th><th>Party</th><th>Bunk</th></tr></thead>
          <tbody>
            {rows.length === 0
              ? <tr><td colSpan={4} style={{ padding: 16, color: "#98979a" }}>No one assigned</td></tr>
              : rows.map((r, i) => (
                <tr key={r.b.slot}><td>{i + 1}</td><td>{r.p!.displayName}</td><td>{crewPartyLabel(r.p!.party)}</td><td>{bunkCode(r.b.slot)}</td></tr>
              ))}
          </tbody>
        </table>
        <div className={styles.docGrid}>
          <div className={styles.docCell}><div className={styles.docK}>DRIVER</div><div className={styles.docV}>{dash(v.driver)}</div><div className={styles.docV}>{dash(v.driverPhone)}</div></div>
          <div className={styles.docCell}><div className={styles.docK}>DOOR / BAY</div><div className={styles.docV}>{dash(v.door)} / {dash(v.bay)}</div></div>
        </div>
        <div className={styles.docGrid}>
          <div className={styles.docCell}><div className={styles.docK}>WIFI</div><div className={styles.docV}>{dash(v.wifi)}</div></div>
          {/* The Wi-Fi password is NEVER rendered (screen or print) — copy-only, matching the info panel and
              the §7.5 must-not-happen rule. The printout carries "In the app" instead of the secret. */}
          <div className={styles.docCell}>
            <div className={styles.docK}>PASSWORD</div>
            {v.wifiPassword
              ? <button
                  type="button"
                  className={`${styles.docV} ${styles.mono}`}
                  style={{ cursor: "copy", background: "none", border: "none", font: "inherit", padding: 0 }}
                  onClick={() => { void navigator.clipboard?.writeText(v.wifiPassword!); pushToast("Copied", "success"); }}
                >Copy</button>
              : <div className={styles.docV}>—</div>}
          </div>
          {v.lockNote && <div className={styles.docFull}>{v.lockNote}</div>}
        </div>
      </div>
    </Modal>
  );
}
